import { LOGGING, MARKER_POPUP_DISPLAY, MARKER_POPUP_CREDITS, DOMAIN_NAME } from '/settings.js';
import { getCardImagePaths } from '../utils/image-utils.js';
import { openLightbox } from './MarkerPopupCarrouselLightbox.js';
import { enableCarouselNavigation } from '../utils/gesture-utils.js';
import { createIconElement } from '../utils/icon.js';
import { preloadAllCarouselImages } from '../utils/cache/index.js';

// Helper to check if an image exists using Image object
function imageExists(url) {
    return new Promise((resolve) => {
        const img = new window.Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = url;
    });
}

export async function MarkerPopupCarrousel({ images }) {
    if (!images || images.length === 0) return null;

    // Don't preload yet - wait until popup is visible
    // Only the first image will load initially via renderImage(0)

    // Carousel state
    let current = 0;
    let gestureCleanup = null; // Store cleanup function for gestures
    const wrapper = document.createElement('div');
    wrapper.className = 'marker-popup-carrousel';

    // Dots - only show if there are multiple images
    if (images.length > 1) {
        const dots = document.createElement('div');
        dots.className = 'carrousel-dots';
        for (let i = 0; i < images.length; i++) {
            const dot = document.createElement('div');
            dot.className = 'carrousel-dot' + (i === 0 ? ' active' : '');
            dots.appendChild(dot);
        }
        wrapper.appendChild(dots);
    }

    const imgContainer = document.createElement('div');
    imgContainer.className = 'carrousel-image-container';

    // Lightbox Trigger - Conditional based on settings
    if (MARKER_POPUP_DISPLAY.lightbox) {
        // Images are already preloaded by preloadMarkerPopupImages() above
        imgContainer.style.cursor = 'zoom-in'; // Indicate clickability
        imgContainer.addEventListener('click', (e) => {
            e.stopPropagation();
            openLightbox({ images, initialIndex: current });
        });
    }

    wrapper.appendChild(imgContainer);

    // Arrows (SVG, no circle, thick stroke) - only show if there are multiple images
    let leftArrow, rightArrow;
    if (images.length > 1) {
        leftArrow = document.createElement('button');
        leftArrow.className = 'carrousel-arrow left';
        leftArrow.innerHTML = `<svg width="24" height="24" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M28 10L16 22L28 34" stroke="currentColor" stroke-width="4.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
        rightArrow = document.createElement('button');
        rightArrow.className = 'carrousel-arrow right';
        rightArrow.innerHTML = `<svg width="24" height="24" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16 10L28 22L16 34" stroke="currentColor" stroke-width="4.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

        // Stop propagation to prevent opening lightbox when clicking arrows
        leftArrow.addEventListener('click', (e) => e.stopPropagation());
        rightArrow.addEventListener('click', (e) => e.stopPropagation());

        wrapper.appendChild(leftArrow);
        wrapper.appendChild(rightArrow);
    }

    // Render image and update dots
    function renderImage(idx) {
        // Validation: Ensure index is within bounds
        if (idx < 0 || idx >= images.length) return;

        const imgData = images[idx];
        const imageId = imgData.id; // Use image ID instead of filename
        const imagePaths = getCardImagePaths(imageId, 'webp');

        // Check if this image is already rendered (reuse if exists)
        let picture = imgContainer.querySelector(`.carrousel-image[data-image-id="${imageId}"]`);

        if (picture) {
            // Image already exists in DOM - just activate it (no new network request!)
            const prevActive = imgContainer.querySelector('.carrousel-image.active');
            if (prevActive) {
                prevActive.classList.remove('active');
            }
            picture.classList.add('active');

            // Update dots
            if (images.length > 1) {
                const dots = wrapper.querySelector('.carrousel-dots');
                if (dots) {
                    Array.from(dots.children).forEach((dot, i) => {
                        dot.classList.toggle('active', i === idx);
                    });
                }
            }
            return; // Early return - no new image creation needed!
        }

        // Previous active image (if any)
        const prevActive = imgContainer.querySelector('.carrousel-image.active');

        // Create new picture element (only if not already in DOM)
        picture = document.createElement('picture');
        picture.className = 'carrousel-image';
        picture.setAttribute('data-image-id', imageId); // Mark with image ID for reuse
        picture.style.position = 'absolute';
        picture.style.top = '0';
        picture.style.left = '0';

        const source = document.createElement('source');
        source.srcset = imagePaths.webp;
        source.type = 'image/webp';
        picture.appendChild(source);

        const img = document.createElement('img');
        // Use WebP as primary source (preloaded, browser will use cache)
        img.src = imagePaths.webp;
        img.alt = imgData.alt_text || '';
        // Use 'eager' since images are preloaded - instant display from cache
        img.loading = 'eager';
        picture.appendChild(img);

        // Copyright overlay (inside picture so it fades with image)
        // Respect MARKER_POPUP_CREDITS settings: show general credits or our credits
        if (imgData.copyright) {
            // Check if this is "our" credit (domain name) or external photographer
            const isOurCredit = imgData.copyright === DOMAIN_NAME || imgData.copyright.toLowerCase().includes(DOMAIN_NAME.toLowerCase());
            const shouldShow = isOurCredit ? MARKER_POPUP_CREDITS.our_credits : MARKER_POPUP_CREDITS.general_credits;

            if (shouldShow) {
                const copyrightDiv = document.createElement('div');
                copyrightDiv.className = 'carrousel-copyright';
                // Add camera icon (sprite)
                const cameraIcon = createIconElement('camera-solid', 'carrousel-copyright-camera');
                copyrightDiv.appendChild(cameraIcon);
                if (imgData.copyright_url) {
                    const link = document.createElement('a');
                    link.href = imgData.copyright_url;
                    link.target = '_blank';
                    link.rel = 'noopener noreferrer';
                    link.textContent = imgData.copyright;
                    // Stop propagation to prevent opening lightbox when clicking copyright link
                    link.addEventListener('click', (e) => e.stopPropagation());
                    copyrightDiv.appendChild(link);
                } else {
                    const textNode = document.createTextNode(imgData.copyright);
                    copyrightDiv.appendChild(textNode);
                }
                picture.appendChild(copyrightDiv);
            }
        }

        // Append new image (hidden initially)
        imgContainer.appendChild(picture);

        // Force reflow then activate for transition
        void picture.offsetWidth; // reflow
        picture.classList.add('active');

        // After transition, hide previous image (but keep in DOM for reuse)
        if (prevActive) {
            prevActive.classList.remove('active');
            // Don't remove from DOM immediately - keep for potential reuse
            // Only remove if we have too many cached images (memory management)
            setTimeout(() => {
                if (prevActive && prevActive.parentElement) {
                    // Keep last 3 images in DOM for quick navigation
                    const allImages = imgContainer.querySelectorAll('.carrousel-image:not(.active)');
                    if (allImages.length > 3) {
                        // Remove oldest non-active image
                        allImages[0].remove();
                    }
                }
            }, 500); // match CSS transition duration
        }

        // Update dots - only if dots exist (multiple images)
        if (images.length > 1) {
            const dots = wrapper.querySelector('.carrousel-dots');
            if (dots) {
                Array.from(dots.children).forEach((dot, i) => {
                    dot.classList.toggle('active', i === idx);
                });
            }
        }
    }

    // Initial render - only first image loads now
    renderImage(current);

    // Preload all remaining images AFTER popup is visible/opened
    // Strategy: Use IntersectionObserver + fallback when attached to DOM
    if (images.length > 1) {
        let preloaded = false;

        const doPreload = () => {
            if (!preloaded) {
                preloaded = true;
                // Preload remaining images (skip first one - it's already loading via renderImage)
                // Pass imgContainer so preload function can check for existing images in DOM
                // NOTE: We ONLY preload with fetch() - we DON'T create DOM elements
                // When renderImage() creates elements on-demand, they'll use cached images (cache hits)
                const imagesToPreload = images.slice(1); // Skip first image
                if (imagesToPreload.length > 0) {
                    preloadAllCarouselImages(imagesToPreload, false, imgContainer).catch(() => {
                        // Silently fail
                    });
                }
            }
        };

        // Method 1: IntersectionObserver - when carousel becomes visible
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    doPreload();
                    observer.disconnect();
                }
            });
        }, { threshold: 0.1 });

        // Method 2: Fallback - when wrapper is attached to DOM
        // Check if already in DOM, or wait for it
        const checkAndObserve = () => {
            if (wrapper.isConnected) {
                observer.observe(wrapper);
                // Also preload after a short delay as fallback (popup might be visible immediately)
                setTimeout(doPreload, 200);
            } else {
                // Not in DOM yet, check again
                setTimeout(checkAndObserve, 50);
            }
        };

        checkAndObserve();
    }

    // Arrow handlers - only if arrows exist (multiple images)
    if (leftArrow && rightArrow) {
        leftArrow.onclick = () => {
            current = (current - 1 + images.length) % images.length;
            renderImage(current);
        };
        rightArrow.onclick = () => {
            current = (current + 1) % images.length;
            renderImage(current);
        };
    }

    // Dot click handlers - only if dots exist (multiple images)
    if (images.length > 1) {
        const dots = wrapper.querySelector('.carrousel-dots');
        if (dots) {
            Array.from(dots.children).forEach((dot, i) => {
                dot.onclick = (e) => {
                    e.stopPropagation(); // Prevent lightbox
                    current = i;
                    renderImage(current);
                };
            });
        }
    }

    // Enable gesture navigation (swipe and keyboard) - only if multiple images
    if (images.length > 1) {
        // Wait for element to be in DOM before attaching gestures
        // Use a small delay to ensure element is fully inserted and rendered
        const setupGestures = () => {
            // Check if element is connected and image container exists
            if (!wrapper.isConnected || !imgContainer) {
                // Retry if not ready yet
                setTimeout(setupGestures, 50);
                return;
            }

            const onNavigateLeft = () => {
                current = (current - 1 + images.length) % images.length;
                renderImage(current);
            };

            const onNavigateRight = () => {
                current = (current + 1) % images.length;
                renderImage(current);
            };

            // Check if popup is active (visible)
            // Simplified: just check if wrapper is connected and has a visible offset parent
            // This ensures the carousel is actually rendered and visible
            const isActive = () => {
                return wrapper.isConnected && wrapper.offsetParent !== null;
            };

            // Enable gestures on the wrapper (entire carousel area) for better touch coverage
            // This ensures swipes work anywhere on the carousel, not just the image
            // Ignore swipes in cards boundary (bottom 150px + safe area) to prevent conflicts
            gestureCleanup = enableCarouselNavigation(
                wrapper,
                onNavigateLeft,
                onNavigateRight,
                {
                    enableSwipe: true,
                    enableKeyboard: true,
                    isActive: isActive,
                    ignoreCardsBoundary: true
                }
            );
        };

        // Try immediately, then retry if needed
        requestAnimationFrame(() => {
            requestAnimationFrame(setupGestures);
        });
    }

    return wrapper;
}
